import cn from "classnames";
import Loader from "components/shared/loader";
import Select from "components/shared/select";
import { fullName } from "helpers/user";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { overallMailReportUsersList } from "sources/books/employees";
import {
    addUserToSendOverallMailReport,
    getOverallMailReportUsers,
    removeUserFromSendOverallMailReport,
} from "sources/profile/organization";

const formatUserName = (user) => {
    const name = fullName({
        firstName: user?.firstName,
        lastName: user?.lastName,
        patronymic: user?.patronymic,
    });

    return name || user?.fullName || user?.email || "Пользователь";
};

export default function OverallMailReportUsers({ roles, profile, organization, modal }) {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedUserId, setSelectedUserId] = useState();

    const currentUserId = profile?.id;
    const canViewList = !!roles?.isTruckingOrganizationAdmin;
    const canManageOthers = canViewList && currentUserId === organization?.adminId;
    const canManageSelf =
        !!currentUserId &&
        !roles?.isIntegrationAccount &&
        (roles?.isTruckingOrganizationAdmin || roles?.isTruckingOrganizationHeadLogist);
    const isVisible = canViewList || canManageSelf;

    const currentUser = useMemo(() => users.find((item) => item.id === currentUserId), [users, currentUserId]);
    const isCurrentUserSubscribed = !!currentUser;

    const getItems = useCallback(() => {
        setLoading(true);
        return getOverallMailReportUsers().then(
            (data) => {
                setUsers(Array.isArray(data) ? data : []);
                setLoading(false);
                return data;
            },
            (error) => {
                setLoading(false);
                throw error;
            },
        );
    }, []);

    useEffect(() => {
        if (isVisible) {
            getItems();
        }
    }, [getItems, isVisible]);

    if (!isVisible) {
        return null;
    }

    const addUser = (userId = selectedUserId) => {
        if (!userId) {
            return;
        }

        setLoading(true);
        return addUserToSendOverallMailReport({ userId })
            .then((res) => {
                if (res) {
                    setSelectedUserId(undefined);
                    return getItems();
                }
            })
            .finally(() => setLoading(false));
    };

    const removeUser = (userId) => {
        const action = () => {
            setLoading(true);
            return removeUserFromSendOverallMailReport({ userId })
                .then((res) => {
                    if (res) {
                        return getItems();
                    }
                })
                .finally(() => setLoading(false));
        };

        if (modal) {
            return modal({
                name: "confirm",
                title: "Отключить ежедневный отчет?",
                text: "Пользователь перестанет получать ежедневный отчет на почту.",
                buttons: { confirm: "Да", cancel: "Нет" },
                action,
            });
        }

        return action();
    };

    const renderAddUser = () => (
        <div className="d-flex align-items-start mb-3">
            <div className="flex-grow-1">
                <Select
                    inputProps={{ name: "overallMailReportUserId" }}
                    url={overallMailReportUsersList}
                    value={selectedUserId}
                    onChange={({ value }) => setSelectedUserId(value)}
                    propName="fullNameOrEmail"
                    excludeItems={users.map((item) => item.id)}
                    placeholder="Выберите пользователя"
                />
            </div>
            <button
                type="button"
                className={cn("btn btn-brand btn-sm ml-3", { disabled: !selectedUserId || loading })}
                disabled={!selectedUserId || loading}
                onClick={() => addUser()}
            >
                Добавить
            </button>
        </div>
    );

    const renderSelfToggle = () => (
        <div className="d-flex align-items-center justify-content-between mb-3">
            <div>
                <div>{formatUserName(currentUser || profile)}</div>
                <small className="text-muted">
                    {isCurrentUserSubscribed ? "Получает ежедневный отчет" : "Не получает ежедневный отчет"}
                </small>
            </div>
            <button
                type="button"
                className={cn("btn btn-sm", isCurrentUserSubscribed ? "btn-outline-danger" : "btn-brand")}
                disabled={loading}
                onClick={() => (isCurrentUserSubscribed ? removeUser(currentUserId) : addUser(currentUserId))}
            >
                {isCurrentUserSubscribed ? "Отключить" : "Включить"}
            </button>
        </div>
    );

    const renderList = () => {
        if (!users.length) {
            return <div className="panel__empty-msg">Получатели не выбраны</div>;
        }

        return (
            <table className="table table-sm mb-0">
                <thead className="thead-light">
                    <tr>
                        <th>#</th>
                        <th>Пользователь</th>
                        <th>Email</th>
                        <th />
                    </tr>
                </thead>
                <tbody>
                    {users.map((item, i) => {
                        const canRemove = canManageOthers || item.id === currentUserId;

                        return (
                            <tr key={item.id}>
                                <td>{i + 1}</td>
                                <td>{formatUserName(item)}</td>
                                <td>{item.email || "-"}</td>
                                <td className="text-right">
                                    {canRemove && (
                                        <button
                                            type="button"
                                            className="btn btn-xs btn-outline-danger"
                                            disabled={loading}
                                            onClick={() => removeUser(item.id)}
                                        >
                                            Отключить
                                        </button>
                                    )}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        );
    };

    return (
        <div className={cn({ loading })}>
            {loading && !users.length ? (
                <Loader prefix="margin-center" />
            ) : (
                <>
                    {canManageOthers && renderAddUser()}
                    {canViewList ? (
                        <>
                            {canManageSelf && !isCurrentUserSubscribed && renderSelfToggle()}
                            {renderList()}
                        </>
                    ) : (
                        renderSelfToggle()
                    )}
                </>
            )}
        </div>
    );
}
